import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deployments } from "@/api/endpoints";
import { ApiError } from "@/api/client";
import type {
  Allocation,
  Deployment,
  DeploymentStatus,
  FunnelKey,
  ReadyInput,
  ReadyInputs,
} from "@/types/api";
import { canEdit, useCurrentUser } from "@/hooks/useAuth";
import { useToast } from "@/store/toastStore";
import { capacityLabel, formatDate } from "@/lib/format";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Select,
  Spinner,
  TextInput,
} from "@/components/ui";

/* --------------------------------------------------------------- helpers */

function errMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  if (e instanceof Error) return e.message;
  return "Something went wrong";
}

const STATUS_COLORS: Record<DeploymentStatus, "slate" | "blue" | "green" | "amber"> = {
  draft: "slate",
  ready: "blue",
  deployed: "green",
  archived: "amber",
};

const FUNNEL_LABELS: Record<FunnelKey, string> = {
  customer: "Customer",
  hardware: "Hardware",
  financing: "Financing",
};

/* ====================================================== Create deployment */

function CreateDeploymentCard({
  editable,
  onCreated,
}: {
  editable: boolean;
  onCreated: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [gpuTypeId, setGpuTypeId] = useState("");

  const gpuQuery = useQuery({
    queryKey: ["deployment-gpu-types"],
    queryFn: () => deployments.gpuTypes(),
  });

  const gpuOptions = useMemo(
    () => [
      { value: "", label: "— Select GPU type —" },
      ...(gpuQuery.data ?? []).map((g) => ({ value: g.id, label: g.name })),
    ],
    [gpuQuery.data],
  );

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => deployments.create(body),
    onSuccess: (dep) => {
      qc.invalidateQueries({ queryKey: ["deployments"] });
      toast("success", "Deployment created");
      setName("");
      setStartDate("");
      setGpuTypeId("");
      onCreated(dep.id);
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  if (!editable) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const t = name.trim();
    if (!t) {
      toast("error", "Name is required");
      return;
    }
    const body: Record<string, unknown> = { name: t };
    body.start_date = startDate ? startDate : null;
    if (gpuTypeId) body.gpu_type_id = gpuTypeId;
    create.mutate(body);
  };

  return (
    <Card title="New deployment">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Name">
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Deployment name"
          />
        </Field>
        <Field label="Start date">
          <TextInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>
        <Field label="GPU type">
          <Select
            value={gpuTypeId}
            onChange={(e) => setGpuTypeId(e.target.value)}
            options={gpuOptions}
            disabled={gpuQuery.isLoading}
          />
        </Field>
        <div className="sm:col-span-3">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? <Spinner /> : "Create deployment"}
          </Button>
        </div>
      </form>
    </Card>
  );
}

/* ====================================================== Deployments table */

function DeploymentsTable({
  rows,
  selectedId,
  onSelect,
}: {
  rows: Deployment[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <Card title="Deployments" className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
              <th className="px-2 py-2 font-medium">Name</th>
              <th className="px-2 py-2 font-medium">Status</th>
              <th className="px-2 py-2 font-medium">GPU type</th>
              <th className="px-2 py-2 font-medium">Readiness</th>
              <th className="px-2 py-2 font-medium">Tempo</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const ready = d.readiness?.ready ?? false;
              return (
                <tr
                  key={d.id}
                  onClick={() => onSelect(d.id)}
                  className={`cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 ${
                    d.id === selectedId ? "bg-slate-50" : ""
                  }`}
                >
                  <td className="px-2 py-2 font-medium text-slate-900">{d.name}</td>
                  <td className="px-2 py-2">
                    <Badge color={STATUS_COLORS[d.status]}>{d.status}</Badge>
                  </td>
                  <td className="px-2 py-2 text-slate-600">{d.gpu_type_name ?? "—"}</td>
                  <td className="px-2 py-2">
                    {d.readiness ? (
                      <Badge color={ready ? "green" : "amber"}>
                        {ready ? "Ready" : "Incomplete"}
                      </Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {d.tempo_url ? (
                      <a
                        href={d.tempo_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-blue-600 hover:underline"
                      >
                        Open
                      </a>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ====================================================== Readiness panel */

function Checkmark({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <span
        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs ${
          ok ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-400"
        }`}
      >
        {ok ? "✓" : "—"}
      </span>
      <span className={ok ? "text-slate-900" : "text-slate-500"}>{label}</span>
    </div>
  );
}

function ReadinessPanel({ dep }: { dep: Deployment }) {
  const r = dep.readiness;
  return (
    <Card title="Readiness">
      {!r ? (
        <EmptyState message="No readiness information" />
      ) : (
        <div className="space-y-3">
          <Badge color={r.ready ? "green" : "amber"}>
            {r.ready ? "Ready to deploy" : "Not ready"}
          </Badge>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <Checkmark ok={r.has_customer} label="Customer" />
            <Checkmark ok={r.has_hardware} label="Hardware" />
            <Checkmark ok={r.has_financing} label="Financing" />
          </div>
          {r.missing.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-slate-500">Missing</div>
              <ul className="list-inside list-disc text-sm text-slate-600">
                {r.missing.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/* ====================================================== Allocations list */

function AllocationsCard({ dep, editable }: { dep: Deployment; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();

  const remove = useMutation({
    mutationFn: (allocId: string) => deployments.removeAllocation(dep.id, allocId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deployment", dep.id] });
      qc.invalidateQueries({ queryKey: ["deployments"] });
      qc.invalidateQueries({ queryKey: ["ready-inputs"] });
      toast("success", "Allocation removed");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  return (
    <Card title="Allocations">
      {dep.allocations.length === 0 ? (
        <EmptyState message="No allocations yet" />
      ) : (
        <ul className="divide-y divide-slate-200">
          {dep.allocations.map((a: Allocation) => (
            <li key={a.id} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <Badge color="violet">{FUNNEL_LABELS[a.funnel_key]}</Badge>
                  <span className="truncate text-sm font-medium text-slate-900">
                    {a.opportunity_title ?? a.opportunity_id}
                  </span>
                </div>
                <div className="mt-0.5 text-xs text-slate-500">
                  {capacityLabel(a.allocated_amount, a.allocated_unit)}
                </div>
              </div>
              {editable && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => remove.mutate(a.id)}
                  disabled={remove.isPending}
                >
                  Remove
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ====================================================== Ready-inputs picker */

function ReadyInputRow({ depId, input }: { depId: string; input: ReadyInput }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [amount, setAmount] = useState(
    input.available_capacity != null ? String(input.available_capacity) : "",
  );

  useEffect(() => {
    setAmount(input.available_capacity != null ? String(input.available_capacity) : "");
  }, [input.available_capacity]);

  const add = useMutation({
    mutationFn: (n: number) =>
      deployments.addAllocation(
        depId,
        input.opportunity_id,
        n,
        input.capacity_unit ?? undefined,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deployment", depId] });
      qc.invalidateQueries({ queryKey: ["deployments"] });
      qc.invalidateQueries({ queryKey: ["ready-inputs"] });
      toast("success", "Allocated");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const submit = () => {
    const raw = amount.trim();
    const n = Number(raw);
    if (raw === "" || Number.isNaN(n) || n <= 0) {
      toast("error", "Enter a valid amount");
      return;
    }
    add.mutate(n);
  };

  return (
    <li className="flex items-end justify-between gap-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-slate-900">{input.title}</div>
        <div className="mt-0.5 text-xs text-slate-500">
          {input.stage_name}
          <span className="text-slate-300"> · </span>
          Available {capacityLabel(input.available_capacity, input.capacity_unit)}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <div className="w-28">
          <TextInput
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="Amount"
          />
        </div>
        <Button size="sm" onClick={submit} disabled={add.isPending}>
          {add.isPending ? <Spinner /> : "Allocate"}
        </Button>
      </div>
    </li>
  );
}

function ReadyInputsCard({ depId, editable }: { depId: string; editable: boolean }) {
  const inputsQuery = useQuery({
    queryKey: ["ready-inputs"],
    queryFn: () => deployments.readyInputs(),
  });

  if (!editable) return null;

  const data = inputsQuery.data;
  const groups: { key: FunnelKey; items: ReadyInput[] }[] = data
    ? (["customer", "hardware", "financing"] as (keyof ReadyInputs)[]).map((k) => ({
        key: k as FunnelKey,
        items: data[k],
      }))
    : [];
  const total = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <Card title="Allocate ready inputs">
      {inputsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> Loading ready inputs…
        </div>
      ) : inputsQuery.isError ? (
        <EmptyState message={errMessage(inputsQuery.error)} />
      ) : total === 0 ? (
        <EmptyState message="No ready inputs available to allocate" />
      ) : (
        <div className="space-y-4">
          {groups.map((g) =>
            g.items.length === 0 ? null : (
              <div key={g.key}>
                <div className="mb-1 text-xs font-medium text-slate-500">
                  {FUNNEL_LABELS[g.key]}
                </div>
                <ul className="divide-y divide-slate-200">
                  {g.items.map((input) => (
                    <ReadyInputRow
                      key={input.opportunity_id}
                      depId={depId}
                      input={input}
                    />
                  ))}
                </ul>
              </div>
            ),
          )}
        </div>
      )}
    </Card>
  );
}

/* ====================================================== Push panel */

function PushCard({ dep, editable }: { dep: Deployment; editable: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const ready = dep.readiness?.ready ?? false;
  const linked = !!dep.tempo_deployment_id;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["deployment", dep.id] });
    qc.invalidateQueries({ queryKey: ["deployments"] });
  };

  const push = useMutation({
    mutationFn: () => deployments.push(dep.id),
    onSuccess: (res) => {
      invalidate();
      toast(
        "success",
        res.created ? "Deployment pushed to Tempo" : "Deployment updated in Tempo",
      );
      if (!res.owner_applied) {
        toast("info", res.warning ?? "Owner could not be applied in Tempo");
      }
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  const unlink = useMutation({
    mutationFn: () => deployments.unlink(dep.id),
    onSuccess: () => {
      invalidate();
      toast("success", "Unlinked from Tempo");
    },
    onError: (e) => toast("error", errMessage(e)),
  });

  if (!editable) return null;

  return (
    <Card title="Tempo">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => push.mutate()}
            disabled={!ready || push.isPending}
            title={ready ? undefined : "Deployment is not ready"}
          >
            {push.isPending ? <Spinner /> : linked ? "Re-push to Tempo" : "Push to Tempo"}
          </Button>
          {linked && (
            <Button
              variant="secondary"
              onClick={() => unlink.mutate()}
              disabled={unlink.isPending}
            >
              {unlink.isPending ? <Spinner /> : "Unlink"}
            </Button>
          )}
        </div>

        {!ready && (
          <p className="text-xs text-amber-600">
            Resolve missing readiness items before pushing.
          </p>
        )}

        {dep.tempo_url && (
          <div className="text-sm">
            <span className="text-slate-500">Tempo deployment: </span>
            <a
              href={dep.tempo_url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 hover:underline"
            >
              {dep.tempo_url}
            </a>
          </div>
        )}

        {dep.last_push_at && (
          <div className="text-xs text-slate-400">
            Last push {formatDate(dep.last_push_at)}
            {dep.last_push_status ? ` · ${dep.last_push_status}` : ""}
          </div>
        )}
      </div>
    </Card>
  );
}

/* ====================================================== Selected detail */

function SelectedDeployment({ depId, editable }: { depId: string; editable: boolean }) {
  const depQuery = useQuery({
    queryKey: ["deployment", depId],
    queryFn: () => deployments.get(depId),
  });

  if (depQuery.isLoading) {
    return (
      <Card title="Deployment">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> Loading deployment…
        </div>
      </Card>
    );
  }

  if (depQuery.isError) {
    return (
      <Card title="Deployment">
        <EmptyState message={errMessage(depQuery.error)} />
      </Card>
    );
  }

  const dep = depQuery.data;
  if (!dep) {
    return (
      <Card title="Deployment">
        <EmptyState message="Deployment not found" />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{dep.name}</h2>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <Badge color={STATUS_COLORS[dep.status]}>{dep.status}</Badge>
              <span className="text-slate-300">·</span>
              <span>{dep.gpu_type_name ?? "No GPU type"}</span>
              {dep.start_date && (
                <>
                  <span className="text-slate-300">·</span>
                  <span>Starts {formatDate(dep.start_date)}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>

      <ReadinessPanel dep={dep} />
      <PushCard dep={dep} editable={editable} />
      <AllocationsCard dep={dep} editable={editable} />
      <ReadyInputsCard depId={dep.id} editable={editable} />
    </div>
  );
}

/* ====================================================== Page */

export default function Assembly() {
  const user = useCurrentUser();
  const editable = canEdit(user?.role);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useQuery({
    queryKey: ["deployments"],
    queryFn: () => deployments.list(),
  });

  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  // Keep a valid selection: clear if the selected deployment disappears.
  useEffect(() => {
    if (selectedId && !rows.some((d) => d.id === selectedId)) {
      setSelectedId(null);
    }
  }, [rows, selectedId]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Create Deployment</h1>
        <p className="mt-1 text-sm text-slate-500">
          Combine ready customer, hardware, and financing inputs into a deployment and push it to Tempo.
        </p>
      </div>

      <CreateDeploymentCard editable={editable} onCreated={setSelectedId} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          {listQuery.isLoading ? (
            <Card title="Deployments">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Spinner /> Loading deployments…
              </div>
            </Card>
          ) : listQuery.isError ? (
            <Card title="Deployments">
              <EmptyState message={errMessage(listQuery.error)} />
            </Card>
          ) : rows.length === 0 ? (
            <Card title="Deployments">
              <EmptyState message="No deployments yet" />
            </Card>
          ) : (
            <DeploymentsTable
              rows={rows}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          )}
        </div>

        <div>
          {selectedId ? (
            <SelectedDeployment depId={selectedId} editable={editable} />
          ) : (
            <Card title="Deployment">
              <EmptyState message="Select a deployment to view details" />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
